import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from "@tanstack/react-query";
import useExportData from '../../../hooks/useExportData';
import { useSorting } from '../../../hooks/useSorting';
import useDebounce from '../../../hooks/useDebounce';
import useCleanPaginationURL from '../../../hooks/useCleanPaginationURL';
import { usePaginationTextReplace } from '../../../hooks/usePaginationTextReplace';
import { useUserContext } from '../../../context/user';
import { PostResponse, PostOrderingField } from '../../../types/api/forum';
import { useDeletePostsMutation } from '../../../hooks/useForumQuery';
import { useEntityFetcher } from '../../../hooks/useEntityFetcher';
import { exportPosts } from '../../../services/forum';
import { fetchCategory } from '../../../services/categories';
import { fetchSubcategory } from '../../../services/catalogs';
import { fetchTopic } from '../../../services/forum';
import { CategoryResponse } from '../../../types/api/categories';
import { SubcategoryResponse } from '../../../types/api/catalogs';
import { TopicResponse } from '../../../types/api/forum';
import { formatDate, toUTCStartOfDay, toUTCEndOfDay } from '../../../helpers/dates';
import { Placeholder, createErrorPlaceholder } from '../../basic';
import { useAlert } from '../../../context/alert';
import TableActionsBanner from '../../common/TableActionsBanner';
import { ForumCommentModal } from './components/ForumCommentModal';
import RegularPageLayout from '../../../layouts/RegularPageLayout';
import { UserTableRow } from '../../basic/UserTableRow';
import { DateFormModal } from '../../user/Ads/components/FilterModal/DateFormModal';
import { commentsTableConfig } from './ForumCommentsList+Table';
import { renderTruncatedText } from '../../../helpers/strings';
import { usePostsQuery } from '../../../hooks/useForumQuery';
import { pageSize } from '../../../services/settings';
import { TextInput, Stack, Checkbox, Breadcrumbs, Button, Text, Spinner, IconButton } from '@primer/react'
import { Table, DataTable } from '@primer/react/experimental'
import { SearchIcon, DownloadIcon, XCircleIcon, XCircleFillIcon, XIcon, FilterIcon } from '@primer/octicons-react'

const EMPTY_SET = new Set<string>();

export default function ForumCommentsList() {
    type CommentAction = 'block'

    const { profile } = useUserContext();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [topicID, setTopicID] = useState<string | undefined>(undefined);
    const [parentCategoryID, setParentCategoryID] = useState<string | undefined>(undefined);
    const [subcategoryID, setSubcategoryID] = useState<string | undefined>(undefined);

    const [parentCategory, setParentCategory] = useState<CategoryResponse | undefined>(undefined);
    const [subcategory, setSubcategory] = useState<SubcategoryResponse | undefined>(undefined);
    const [topic, setTopic] = useState<TopicResponse | undefined>(undefined);
    const [selectedComment, setSelectedComment] = useState<PostResponse | undefined>(undefined);


    const [pageIndex, setPageIndex] = React.useState(1)

    const [searchQuery, setSearchQuery] = useState<string>('');
    const debouncedSearchQuery = useDebounce(searchQuery, 500)

    const [filterOpened, setFilterOpened] = useState<boolean>(false);

    // Comments
    const [openDisplayCommentModal, setOpenDisplayCommentModal] = React.useState(false)

    // Filters Modal
    const [dateFilterOpened, setDateFilterOpened] = useState<boolean>(false);

    // Filter request params
    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');

    // Filter draft params for modal
    const [fromDateDraft, setFromDateDraft] = useState<string>('');
    const [toDateDraft, setToDateDraft] = useState<string>('');

    const createdFromIsoDate = fromDate ? toUTCStartOfDay(fromDate) : undefined;
    const createdToIsoDate = toDate ? toUTCEndOfDay(toDate) : undefined;

    // Выделение в таблицк
    const [selectedRows, setSelectedRows] = useState<Set<string>>(EMPTY_SET);
    // Один выделенный комментарий, когда на нем (не через алерт) нажимается подтверждение или блок
    const currentActionAdRef = useRef<PostResponse | null>(null);
    const [actionsAlertId, setActionsAlertId] = useState<string | undefined>(undefined);

    const queryClient = useQueryClient();

    const { isLoading: isLoadingExport, exportAction } = useExportData();
    const { setSuccess, setInfo, addCustomAlert, updateAlert, removeAlert } = useAlert();

    // Loading for Breadcrumbs
    const categoryMutation = useEntityFetcher(fetchCategory, setParentCategory);
    const subcategoryMutation = useEntityFetcher(fetchSubcategory, setSubcategory);
    const topicMutation = useEntityFetcher(fetchTopic, setTopic);

    const isLoadingCategory = categoryMutation.isPending;
    const isLoadingSubcategory = subcategoryMutation.isPending;
    const isLoadingTopic = topicMutation.isPending;

    const dateFormError =
        fromDate !== '' &&
        toDate !== '' &&
        new Date(fromDate) > new Date(toDate);

    // Fetch metadata by ids in query url
    useEffect(() => {
        const configs = [
            {
                paramName: 'parentCategoryID',
                currentValue: parentCategoryID,
                setter: setParentCategoryID,
                mutation: categoryMutation,
                isLoading: categoryMutation.isPending,
            },
            {
                paramName: 'subcategoryID',
                currentValue: subcategoryID,
                setter: setSubcategoryID,
                mutation: subcategoryMutation,
                isLoading: subcategoryMutation.isPending,
            },
            {
                paramName: 'topicID',
                currentValue: topicID,
                setter: setTopicID,
                mutation: topicMutation,
                isLoading: topicMutation.isPending,
            }
        ];

        configs.forEach(({ paramName, currentValue, setter, mutation, isLoading }) => {
            const queryValue = searchParams.get(paramName);
            if (queryValue && currentValue !== queryValue && !isLoading) {
                setter(queryValue);
                mutation.mutate(queryValue);
            }
        });
    }, [searchParams, parentCategoryID, subcategoryID, topicID]);

    const isLoadingCategoryMetadata = isLoadingCategory || isLoadingSubcategory || isLoadingTopic

    const {
        orderingParam,
        handleSort,
        getSortIcon,
        getSortText
    } = useSorting<PostOrderingField>("created_at");
    const orderingParams: PostOrderingField = orderingParam as PostOrderingField;

    // Reset pages when filters changed
    useEffect(() => {
        setPageIndex(1);
    }, [fromDate, toDate]);

    useEffect(() => {
        setPageIndex(1);
        setSelectedRows(EMPTY_SET);
    }, [debouncedSearchQuery]);

    useEffect(() => {
        setPageIndex(1);
        setSelectedRows(EMPTY_SET);
    }, [debouncedSearchQuery]);

    const {
        isLoading,
        data,
        refetch,
        isFetched,
        isError
    } = usePostsQuery(
        debouncedSearchQuery, // search
        pageIndex, // page
        pageSize,  // limit
        topicID,  // topicID
        createdFromIsoDate,// createdFrom
        createdToIsoDate,// createdTo
        orderingParams ?? undefined //ordering
    );

    useCleanPaginationURL();
    usePaginationTextReplace()

    useEffect(() => {
        setSelectedRows(EMPTY_SET);
        refetch()
    }, [pageIndex]);

    // POSTS ACTIONS
    const useCommentAction = (action: CommentAction, actionsAlertId?: string) => {
        return useCallback(() => {
            let selectedItems = selectedRowsRef.current;
            if (selectedItems && selectedItems.size > 0) {
                const ids = Array.from(selectedItems);

                switch (action) {
                    case 'block':
                        deleteCommentRequest({ ids: ids });
                        break
                    default:
                        console.error('Unknown action:', action);
                }
            }
        }, [action]);
    };

    const deleteSelectedPosts = useCommentAction('block', actionsAlertId);

    // DELETE POST
    function createConfirmDeleteAlertProps(count: number) {
        return {
            count: count,
            title: 'Точно хотите удалить комментарии?',
            buttons: [
                <Button key="clear-button" variant="default" onClick={dismissConfirmOneAdAlert}>
                    Нет
                </Button>,
                <Button key="clear-button" variant="danger" onClick={deleteSelectedPosts}>
                    Да, удалить
                </Button>
            ],
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined)
                setSelectedRows(EMPTY_SET);
            }
        }
    }

    function createDeleteSingleCommentAlertProps(id: string) {
        return {
            count: undefined,
            title: 'Точно хотите удалить комментарий?',
            buttons: [
                <Button key="clear-button" variant="default" onClick={dismissConfirmOneAdAlert}>
                    Нет
                </Button>,
                <Button key="clear-button" variant="danger" onClick={() => deleteCommentRequest({ ids: [id] })}>
                    Да, удалить
                </Button>
            ],
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined)
            }
        }
    }

    const dismissConfirmOneAdAlert = useCallback(() => {
        setActionsAlertId(undefined)
        dismissTableActionAlert()
    }, [actionsAlertId]);

    // POSTS ACTIONS
    const { mutate: deleteCommentRequest, isPending: isDeletingProcess } = useDeletePostsMutation(() => {
        commentDidDeleted();
    });

    const commentDidDeleted = () => {
        if (selectedRows.size > 1) {
            setInfo('Комментарии удалены')
        } else {
            setInfo('Комментарий удален')
        }

        refetch()
        dismissConfirmOneAdAlert()
        dismissTableActionAlert()
    }

    const showDeleteCommentAlert = useCallback(() => {
        let selectedItemsCount = selectedRowsRef.current.size
        let currentAd = currentActionAdRef.current

        if (selectedItemsCount > 0) {
            let selectedItemsCount = selectedRowsRef.current.size
            const props = createConfirmDeleteAlertProps(selectedItemsCount)
            showUpdatedAlert(props)
        } else if (currentAd) {
            const props = createDeleteSingleCommentAlertProps(currentAd.id)
            const existedAlertId = actionsAlertIdRef.current
            if (existedAlertId) {
                updateAlert(existedAlertId, {
                    customProps: props
                });
            } else {
                const alertId = addCustomAlert(
                    TableActionsBanner,
                    props,
                    {
                        autoClose: false,
                    }
                );

                setActionsAlertId(alertId)
            }
        }
    }, [actionsAlertId]);

    // ALERT HELPERS
    const dismissTableActionAlert = () => {
        let alertId = actionsAlertIdRef.current
        if (alertId) {
            removeAlert(alertId)
            setActionsAlertId(undefined)
            setSelectedRows(EMPTY_SET);
        }
    }

    const actionsAlertIdRef = useRef(actionsAlertId);
    useEffect(() => {
        actionsAlertIdRef.current = actionsAlertId;
    }, [actionsAlertId])

    const selectedRowsRef = useRef(selectedRows);
    useEffect(() => {
        selectedRowsRef.current = selectedRows;
    }, [selectedRows])

    const handleAddTableAlert = useCallback((count: number) => {
        let props: any = undefined

        props = createConfirmDeleteAlertProps(count)

        let alertId = actionsAlertIdRef.current
        if (alertId) {
            updateAlert(alertId, {
                customProps: props
            });
        } else {
            const alertId = addCustomAlert(
                TableActionsBanner,
                props,
                {
                    autoClose: false,
                }
            );

            setActionsAlertId(alertId)
        }
    }, [actionsAlertId, data]);

    const showUpdatedAlert = useCallback((customProps: Record<string, any>) => {
        let selectedItemsCount = selectedRowsRef.current.size
        const alertId = actionsAlertIdRef.current
        if (alertId) {
            updateAlert(alertId, {
                customProps: customProps
            });
        }
    }, [actionsAlertId]);

    useEffect(() => {
    }, [actionsAlertId])

    // SELECT ROWS
    const handleSelectAll = useCallback((isSelected: boolean) => {
        if (data) {
            if (isSelected) {
                setSelectedRows(new Set(data.list.map(row => row.id)));
            } else {
                setSelectedRows(EMPTY_SET);
            }
        }
    }, [data]);

    const headerCheckboxState = useMemo(() => {
        if (data && data.list.length > 0) {
            const active = data.list
            const totalRows = data.list.length;
            const selectedFromCurrentData = active.filter(row => selectedRows.has(row.id)).length;

            if (selectedFromCurrentData === 0) {
                return { checked: false, indeterminate: false };
            } else if (selectedFromCurrentData === totalRows && totalRows > 0) {
                return { checked: true, indeterminate: false };
            } else {
                return { checked: false, indeterminate: true };
            }
        }
        return { checked: false, indeterminate: false };
    }, [data, selectedRows]);

    const handleRowSelect = useCallback((rowId: string, isSelected: boolean) => {
        rowSelect(rowId, isSelected)
    }, [handleAddTableAlert]);

    const rowSelect = useCallback((rowId: string, isSelected: boolean) => {
        setSelectedRows(prev => {
            const newSelection = new Set(prev);
            if (isSelected) {
                newSelection.add(rowId);
            } else {
                newSelection.delete(rowId);
            }
            return newSelection;
        });

    }, [handleAddTableAlert]);

    useEffect(() => {
        let selectedItemsCount = selectedRows.size

        if (selectedItemsCount > 0) {
            handleAddTableAlert(selectedItemsCount)
        } else {
            if (actionsAlertId) {
                removeAlert(actionsAlertId)
                setActionsAlertId(undefined)
            }
        }
    }, [selectedRows])

    // EXPORT POSTS
    const downloadComments = async () => {
        try {
            const result = await exportAction(exportPosts, "comments", "xlsx");
            if (result.success) {
                setSuccess('Загрузка списка комментариев успешно началась');
            }
        } catch (err) { }
    }

    useEffect(() => {
        queryClient.invalidateQueries({ queryKey: [searchQuery, pageIndex] })
    }, [pageIndex]);

    const clearSearchInput = () => {
        setSearchQuery("")
    }

    const openCommentModal = (post: PostResponse) => {
        setSelectedComment(post)
        setOpenDisplayCommentModal(true)
    }

    const commentModalDismissed = () => {
        setSelectedComment(undefined)
        setOpenDisplayCommentModal(false)
    }

    const commentModalDeleted = () => {
        commentModalDismissed()
        refetch()
    }

    // FILTER MODAL
    const handleDismiss = () => {
        setFromDate('');
        setToDate('');

        setFromDateDraft('');
        setToDateDraft('');

        setFilterOpened(false);
    }
    const handleApply = () => {
        setFromDate(fromDateDraft);
        setToDate(toDateDraft);

        setFilterOpened(false);
    }

    const emptyListDescription = `По вашему запросу не найдено
подходящих комментариев`;
    const emptySearchDescription = `В сервисе не создано ни одного
     комментария в данном форуме`;

    const blockSingleComment = (ad: PostResponse) => {
        currentActionAdRef.current = ad;
        showDeleteCommentAlert()
    };

    return (
        <RegularPageLayout
            header={
                <Stack justify="space-between" direction="horizontal">
                    {isLoadingCategoryMetadata && <Spinner />}
                    {!isLoadingCategoryMetadata && <Breadcrumbs>
                        <Breadcrumbs.Item href="/forums/">Все разделы</Breadcrumbs.Item>
                        <Breadcrumbs.Item href={"/forums/forum/?id=" + parentCategoryID}>
                            {renderTruncatedText(parentCategory?.title, 32)}
                        </Breadcrumbs.Item>
                        <Breadcrumbs.Item href={"/forums/forum/?id=" + parentCategoryID}>
                            {renderTruncatedText(subcategory?.title, 32)}
                        </Breadcrumbs.Item>
                        <Breadcrumbs.Item href="#" selected>
                            {renderTruncatedText(topic?.title, 32)}
                        </Breadcrumbs.Item>
                    </Breadcrumbs>}

                    {selectedComment && <ForumCommentModal
                        isOpen={openDisplayCommentModal}
                        post={selectedComment}
                        onDidDelete={commentModalDeleted}
                        onClose={commentModalDismissed}
                    />}
                    <Stack direction="horizontal">
                        <TextInput
                            className='h-8'
                            leadingVisual={SearchIcon}
                            trailingAction={
                                searchQuery.length > 0 ? (
                                    <TextInput.Action
                                        onClick={() => {
                                            setSearchQuery('');
                                        }}
                                        icon={XCircleFillIcon}
                                        aria-label="Очистить"
                                    />
                                ) : undefined
                            }
                            value={searchQuery}
                            placeholder='Поиск'
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                            }}
                        />
                        <Button
                            leadingVisual={DownloadIcon}
                            aria-label="Выгрузить список комментариев"
                            loading={isLoadingExport}
                            onClick={downloadComments}>Выгрузить список комментариев</Button>

                        <IconButton
                            icon={FilterIcon}
                            onClick={() => !dateFilterOpened && setDateFilterOpened(true)}
                            aria-label="Фильтр" />

                        <DateFormModal
                            dateFilterOpened={dateFilterOpened}
                            setDateFilterOpened={setDateFilterOpened}
                            fromDate={fromDateDraft}
                            setFromDate={setFromDateDraft}
                            toDate={toDateDraft}
                            setToDate={setToDateDraft}
                            dateFormError={dateFormError}
                            onDismiss={handleDismiss}
                            onApply={handleApply}
                            isShowBackButton={false}
                        />
                    </Stack>
                </Stack>
            }
            content={
                <Stack>

                    {isError && !isLoading && createErrorPlaceholder()}
                    {isLoading && <Table.Container>
                        <Table.Skeleton
                            aria-labelledby="posts-loading"
                            rows={pageSize}
                            columns={commentsTableConfig().map((el) => {
                                return {
                                    header: el.header,
                                    id: el.field,
                                }
                            })}
                        />
                    </Table.Container>}
                    {isFetched && data?.list.length === 0 && (
                        <Placeholder
                            icon={<XCircleIcon size={24} className='opacity-40' />}
                            header={searchQuery.length > 0 ? 'Ничего не было найдено' : 'Комментарии не найдены'}
                            description={searchQuery.length > 0 ? emptySearchDescription : emptyListDescription}
                            button={searchQuery.length > 0 && <Button
                                variant="default"
                                leadingVisual={XIcon}
                                onClick={clearSearchInput}>
                                Очистить</Button>}
                        />
                    )}
                    {isFetched && data && data.list.length > 0 &&
                        <Table.Container>
                            <DataTable
                                aria-labelledby="posts-table"
                                data={data.list}
                                columns={[
                                    {
                                        id: 'select',
                                        header: (() => <Checkbox
                                            checked={headerCheckboxState.checked}
                                            indeterminate={headerCheckboxState.indeterminate}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                handleSelectAll(e.target.checked)
                                            }
                                            aria-label="Выделить все строки"
                                        />),
                                        field: undefined,
                                        rowHeader: true,
                                        maxWidth: 40,
                                        renderCell: (post: PostResponse) => {
                                            return <Checkbox
                                                checked={selectedRows.has(post.id)}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                    handleRowSelect(post.id, e.target.checked)
                                                }
                                                aria-label={`Выделить ${post.id}`}
                                            />
                                        }
                                    },
                                    {
                                        id: 'username',
                                        header: (() =>
                                            <button onClick={() => handleSort("username")}>
                                                <Stack align="center" direction="horizontal" gap='none'>
                                                    {getSortIcon("username")}
                                                    <Text className="ml-[4.5px]">{getSortText("username", 'Пользователь', 'А-Я', 'Я-А')}</Text>
                                                </Stack>
                                            </button>
                                        ),
                                        renderCell: (row) => {
                                            const user = (row as PostResponse).user;
                                            return <UserTableRow
                                                user={user}
                                                onClick={() => navigate('/users/user?id=' + user.id)} />
                                        }
                                    },
                                    {
                                        id: 'phone',
                                        header: ('Номер телефона'),
                                        renderCell: (row) => {
                                            const post = row as PostResponse;
                                            return post.user?.phone
                                        }
                                    },
                                    {
                                        id: 'comment',
                                        header: ('Комментарий'),
                                        renderCell: (row) => {
                                            const post = row as PostResponse;
                                            return <Stack direction="horizontal" align="center">
                                                <Stack gap='none'>
                                                    <Button
                                                        onClick={() => openCommentModal(post)}
                                                        className='!h-[2px] -ml-2'
                                                        size="small"
                                                        variant="invisible">
                                                        {renderTruncatedText(post.content, 64)}
                                                    </Button>
                                                </Stack>
                                            </Stack>
                                        }
                                    },
                                    {
                                        id: 'date',
                                        header: (() =>
                                            <button onClick={() => handleSort('created_at')}>
                                                <Stack align="center" direction="horizontal" gap='none'>
                                                    {getSortIcon('created_at')}
                                                    <Text className="ml-[4.5px]">{getSortText('created_at', 'Дата размещения')}</Text>
                                                </Stack>
                                            </button>
                                        ),
                                        renderCell: (row) => {
                                            const post = row as PostResponse;
                                            return formatDate(post.createdAt)
                                        }
                                    },
                                    {
                                        id: 'actions',
                                        header: (''),
                                        renderCell: (row: PostResponse) => {
                                            return (
                                                <Stack direction="horizontal" gap="condensed" >
                                                    <Button
                                                        onClick={() => blockSingleComment(row)}
                                                        variant="danger"
                                                        size="small"
                                                        disabled={selectedRows.size !== 0}
                                                        className={selectedRows.size !== 0 ? 'opacity-50' : ''}
                                                        aria-label="Заблокировать">Удалить</Button>
                                                </Stack>
                                            )
                                        }
                                    },
                                ]}
                            />
                        </Table.Container>
                    }
                    {isFetched && data && data.list.length > 0 &&
                        (<div className="sticky bottom-0 bg-white border-t border-gray-200 z-10 -mt-4">
                            <Table.Pagination aria-label="pagination" pageSize={pageSize} totalCount={data.totalCount} onChange={({
                                pageIndex
                            }) => {
                                setPageIndex(pageIndex + 1);
                            }} defaultPageIndex={pageIndex - 1} />
                        </div>
                        )}
                </Stack>
            }
        ></RegularPageLayout>

    )
}
